import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { MedicalReferral } from "api/models/MedicalReferral";
import { RecordNoteHeader } from "routes/patients/PatientProfile/components/Information/components/RecordNoteHeader/RecordNoteHeader";
import { MUITextInput } from "shared/atoms/inputs";
import { MUIAccordion, MUIAccordionDetails, MUIAccordionSummary } from "shared/atoms/MUIAccordion";

import { SectionRow } from "../../../DynamicMedicalNotes/components";
import { Form } from "../../../DynamicMedicalNotes/utils/formStyles";

interface Props {
  record: MedicalReferral;
}

export const UkReferralCard = ({ record }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { uk_referral_data, nhs_number, date_of_physician_signature } = record;

  const form = useForm({
    defaultValues: {
      gp: [uk_referral_data?.gp.name, uk_referral_data?.gp.practice, uk_referral_data?.gp.address]
        .filter(Boolean)
        .join(", "),
      site: uk_referral_data?.site ?? "",
      urgency: uk_referral_data?.urgency ?? "",
      nhs_number: nhs_number ?? "",
      body_parts: uk_referral_data?.body_parts_referred.join(", ") ?? "",
      pathways: uk_referral_data?.pathway_ids.join(", ") ?? "",
    },
  });

  return (
    <MUIAccordion>
      <MUIAccordionSummary>
        <RecordNoteHeader date={uk_referral_data?.referral_date ?? date_of_physician_signature}>
          {t("patients.medical_records.referral")}
        </RecordNoteHeader>
      </MUIAccordionSummary>
      <MUIAccordionDetails>
        <FormProvider {...form}>
          <Form>
            <ContentWrapper>
              <Section>
                <SectionRow>
                  <StyledMUITextInput name="gp" label={t("patients.medical_records.uk_referral.gp")} disabled />
                  <StyledMUITextInput name="site" label={t("patients.medical_records.uk_referral.site")} disabled />
                </SectionRow>
                <SectionRow>
                  <StyledMUITextInput
                    name="urgency"
                    label={t("patients.medical_records.uk_referral.urgency")}
                    disabled
                  />
                  <StyledMUITextInput
                    name="nhs_number"
                    label={t("patients.medical_records.uk_referral.nhs_number")}
                    disabled
                  />
                </SectionRow>
                <SectionRow>
                  <StyledMUITextInput
                    name="body_parts"
                    label={t("patients.medical_records.uk_referral.body_parts")}
                    disabled
                  />
                  <StyledMUITextInput
                    name="pathways"
                    label={t("patients.medical_records.uk_referral.pathways")}
                    disabled
                  />
                </SectionRow>
              </Section>
            </ContentWrapper>
          </Form>
        </FormProvider>
      </MUIAccordionDetails>
    </MUIAccordion>
  );
};

const StyledMUITextInput = styled(MUITextInput)`
  flex: 1;
  margin-right: ${props => props.theme.spacing.S_15};
`;

const Section = styled.div`
  padding: ${props => props.theme.spacing.S_20};
  padding-right: 120px;

  ${props => props.theme.belowBreakpoint} {
    padding-right: ${props => props.theme.spacing.S_20};
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
`;
